// src/pages/programManagerDashboard/projectCreation.jsx

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SidebarNav from "../../components/MasterSideBar.jsx"; 
import TopbarNav from "../../components/TopbarNav";
import { toast } from "react-toastify";
import { fetchProjects, fetchProjectStats } from "../../api/projectCreationApi";
import { FiPlus, FiRefreshCw, FiBriefcase } from "react-icons/fi";
import useDebounce from "../../hooks/useDebounce";
import ProjectFormModal from "../../components/addprojectmodal.jsx";
import ProjectsTable from "../../components/ProjectsTable.jsx";
import ProjectDashboardOverview from "../../components/ProjectDashboardOverview";

const ProjectListPage = () => {
  const [projects, setProjects] = useState([]);
  const [projectStats, setProjectStats] = useState({
    totalProjects: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
    planning: 0,
    onHold: 0,
    cancelled: 0,
    archived: 0,
  });
  
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchProjects({ search: debouncedSearchTerm });
      const sorted = [...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setProjects(sorted);
    } catch (error) {
      toast.error("Failed to fetch projects.");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm]);

  const loadProjectStats = useCallback(async () => {
    try {
      const stats = await fetchProjectStats();
      setProjectStats({
        totalProjects: stats.totalProjects || 0,
        inProgress: stats["In Progress"] || 0,
        completed: stats.Completed || 0,
        overdue: stats.overdue || 0,
        planning: stats.Planning || 0,
        onHold: stats["On Hold"] || 0,
        cancelled: stats.Cancelled || 0,
        archived: stats.Archived || 0,
      });
    } catch (error) {
      console.error("Stats fetch error:", error);
    }
  }, []);

  useEffect(() => {
    loadProjects();
    loadProjectStats();
  }, [loadProjects, loadProjectStats]);

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
      {/* 1. Master Sidebar */}
      <SidebarNav visible={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* 2. Main Content Wrapper */}
      <div className="flex-1 flex flex-col md:ml-64 transition-all duration-300 min-w-0">
        
        {/* 3. TopBar */}
        <TopbarNav
          onSidebarToggle={() => setSidebarOpen(true)}
          onSearchChange={setSearchTerm}
          searchTerm={searchTerm}
        />

        <main className="p-4 md:p-8 mt-16 lg:mt-20 space-y-8 overflow-y-auto">
          
          {/* Header Section - Fixed Overlap by using flex-wrap */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-[280px]">
              <h1 className="text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight flex items-center gap-3">
                <FiBriefcase className="text-orange-600 shrink-0" />
                Project Management
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                Monitor lifecycle and deliverables.
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={loadProjects}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-all font-bold text-sm shadow-sm active:scale-95"
              >
                <FiRefreshCw className={loading ? "animate-spin" : ""} />
                Refresh
              </button>

              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-2 px-6 py-2.5 bg-orange-600 text-white rounded-xl font-bold hover:bg-orange-700 shadow-lg shadow-orange-200 active:scale-95 text-sm"
              >
                <FiPlus size={18} />
                New Project
              </button>
            </div>
          </div>

          {/* 4. Dashboard Overview - Fixed width issue */}
          <div className="w-full">
             <ProjectDashboardOverview stats={projectStats} />
          </div>

          {/* 5. Projects Table Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Project Database</h2>
              {loading && <span className="text-xs text-orange-500 animate-pulse font-bold">Refreshing...</span>}
            </div>
            
            <div className="overflow-x-auto p-2">
              <ProjectsTable
                projects={projects}
                onSuccess={() => {
                  loadProjects();
                  loadProjectStats();
                }}
              />
            </div>
          </div>
        </main>
      </div>

      <ProjectFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          loadProjects();
          loadProjectStats();
          setIsModalOpen(false);
        }}
      />
    </div>
  );
};

export default ProjectListPage;